import { Component, Show, createSignal, onMount, For } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import Button from '../../shared/Button'
import TextInput from '../../shared/TextInput'
import { Page } from '/web/Layout'
import { ArrowLeft, Cloud, Clipboard, FileText, Send } from 'lucide-solid'
import { createFeedback, getFeedbacks, updateFeedbackStatus } from '../../store/data/feedback'
import { toastStore } from '../../store'
import { AppSchema } from '/common/types/schema'

const FeedbackPage: Component = () => {
  const nav = useNavigate()
  const [activeTab, setActiveTab] = createSignal<'submit' | 'history'>('submit')
  const [title, setTitle] = createSignal('')
  const [content, setContent] = createSignal('')
  const [feedbacks, setFeedbacks] = createSignal<AppSchema.Feedback[]>([])
  const [statusFilter, setStatusFilter] = createSignal<AppSchema.Feedback['status'] | 'all'>('all')
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [isLoading, setIsLoading] = createSignal(false)

  onMount(() => {
    loadFeedbacks()
  })

  const loadFeedbacks = async () => {
    setIsLoading(true)
    try {
      const status = statusFilter() === 'all' ? undefined : statusFilter()
      const res = await getFeedbacks(status)
      if (res.result) {
        setFeedbacks(res.result.feedbacks)
      }
    } catch (error: any) {
      toastStore.error(`加载反馈记录失败: ${error?.message || error}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!title().trim() || !content().trim()) {
      toastStore.warn('请填写标题和内容')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await createFeedback({
        title: title().trim(),
        content: content().trim(),
      })

      if (res.error) {
        toastStore.error(`提交失败: ${res.error}`)
        return
      }

      toastStore.success('反馈提交成功！我们会尽快处理。')
      setTitle('')
      setContent('')
      setActiveTab('history')
      loadFeedbacks()
    } catch (error: any) {
      toastStore.error(`提交失败: ${error?.message || error}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusCount = (status: AppSchema.Feedback['status']) => {
    return feedbacks().filter((f) => f.status === status).length
  }

  const getStatusText = (status: AppSchema.Feedback['status']) => {
    const map: Record<AppSchema.Feedback['status'], string> = {
      pending: '待处理',
      review: '审核',
      resolved: '解決',
      closed: '关闭',
    }
    return map[status] || status
  }

  const getStatusColor = (status: AppSchema.Feedback['status']) => {
    const map: Record<AppSchema.Feedback['status'], string> = {
      pending: 'text-yellow-500',
      review: 'text-blue-500',
      resolved: 'text-green-500',
      closed: 'text-gray-500',
    }
    return map[status] || ''
  }

  return (
    <Page>
      <div class="flex flex-col min-h-[80vh] px-4 py-6">
        <div class="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div class="flex items-center mb-6">
            <Button
              schema="clear"
              onClick={() => nav('/profile')}
              class="mr-4"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 class="text-3xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              反馈中心
            </h1>
          </div>

          <p class="text-[var(--text-500)] mb-6">感谢您的反馈!我们会尽快处理。</p>

          {/* Tabs */}
          <div class="flex gap-2 mb-6">
            <Button
              schema={activeTab() === 'submit' ? 'primary' : 'secondary'}
              onClick={() => setActiveTab('submit')}
              class={`rounded-lg px-6 py-2 ${
                activeTab() === 'submit'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 border-purple-500'
                  : 'bg-[var(--bg-700)] border-[var(--bg-600)]'
              }`}
            >
              提交反馈
            </Button>
            <Button
              schema={activeTab() === 'history' ? 'primary' : 'secondary'}
              onClick={() => {
                setActiveTab('history')
                loadFeedbacks()
              }}
              class={`rounded-lg px-6 py-2 ${
                activeTab() === 'history'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 border-purple-500'
                  : 'bg-[var(--bg-700)] border-[var(--bg-600)]'
              }`}
            >
              我的反馈记录
            </Button>
          </div>

          {/* Submit Feedback Tab */}
          <Show when={activeTab() === 'submit'}>
            <div class="bg-[var(--bg-800)] rounded-lg border border-[var(--hl-500)] p-6">
              <div class="flex items-center gap-2 mb-4">
                <Cloud size={20} class="text-[var(--text-500)]" />
                <h2 class="text-xl font-semibold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                  意见反馈
                </h2>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm text-[var(--text-500)] mb-2">标题</label>
                  <TextInput
                    fieldName="title"
                    value={title()}
                    onChange={(ev) => setTitle(ev.currentTarget.value)}
                    placeholder="输入标题"
                    class="bg-[var(--bg-700)] border-[var(--bg-600)]"
                  />
                </div>

                <div>
                  <label class="block text-sm text-[var(--text-500)] mb-2">内容</label>
                  <textarea
                    value={content()}
                    onInput={(ev) => setContent(ev.currentTarget.value)}
                    placeholder="输入问题描述"
                    class="w-full h-32 px-4 py-3 bg-[var(--bg-700)] border border-[var(--bg-600)] rounded-lg text-white placeholder-[var(--text-600)] resize-none focus:outline-none focus:border-[var(--hl-500)]"
                  />
                </div>

                <Button
                  schema="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting()}
                  class="w-full rounded-lg py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                >
                  <Send size={18} class="mr-2" />
                  {isSubmitting() ? '提交中...' : '提交'}
                </Button>
              </div>
            </div>
          </Show>

          {/* Feedback History Tab */}
          <Show when={activeTab() === 'history'}>
            <div class="bg-[var(--bg-800)] rounded-lg border border-[var(--hl-500)] p-6">
              <div class="flex items-center gap-2 mb-4">
                <Clipboard size={20} class="text-orange-400" />
                <h2 class="text-xl font-semibold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                  我的反馈记录
                </h2>
              </div>

              {/* Status Filters */}
              <div class="flex gap-2 mb-6 flex-wrap">
                {(['all', 'pending', 'review', 'resolved', 'closed'] as const).map((status) => (
                  <Button
                    schema={statusFilter() === status ? 'primary' : 'secondary'}
                    onClick={() => {
                      setStatusFilter(status)
                      loadFeedbacks()
                    }}
                    class={`rounded-lg px-4 py-1 text-sm ${
                      statusFilter() === status
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                        : 'bg-[var(--bg-700)]'
                    }`}
                  >
                    {status === 'all'
                      ? '全部'
                      : getStatusText(status as AppSchema.Feedback['status'])}
                    {status !== 'all' && (
                      <span class="ml-1">({getStatusCount(status as AppSchema.Feedback['status'])})</span>
                    )}
                  </Button>
                ))}
              </div>

              {/* Feedback List */}
              <Show
                when={!isLoading() && feedbacks().length > 0}
                fallback={
                  <div class="flex flex-col items-center justify-center py-12">
                    <FileText size={48} class="text-orange-400 mb-4" />
                    <p class="text-white">暂无反馈记录</p>
                  </div>
                }
              >
                <div class="space-y-4">
                  <For each={feedbacks()}>
                    {(feedback) => (
                      <div class="bg-[var(--bg-700)] rounded-lg p-4 border border-[var(--bg-600)]">
                        <div class="flex justify-between items-start mb-2">
                          <h3 class="text-lg font-semibold text-white">{feedback.title}</h3>
                          <span
                            class={`text-sm px-2 py-1 rounded ${getStatusColor(feedback.status)}`}
                          >
                            {getStatusText(feedback.status)}
                          </span>
                        </div>
                        <p class="text-[var(--text-400)] mb-2">{feedback.content}</p>
                        <p class="text-xs text-[var(--text-600)]">
                          {new Date(feedback.createdAt).toLocaleString('zh-CN')}
                        </p>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </div>
    </Page>
  )
}

export default FeedbackPage








